import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import './faqs.css';

const Faqs = () => {
  const [selectedFaq, setSelectedFaq] = useState(1);

  const faqs = [
    {
      id: 1,
      question: "What is the purpose of this website?",
      answer: "This website is a carpooling app that connects drivers with empty seats to passengers heading the same way. You can offer a ride if you're driving, request one at your own fare if you're not, or book a seat on someone else's ride."
    },
    {
      id: 2,
      question: "How do I create an account?",
      answer: "Sign up with your email and a password, or use your Google account. You'll receive a verification email — click the link to activate your account before you can post or book rides."
    },
    {
      id: 3,
      question: "How do I offer a ride?",
      answer: "Go to Publish, fill in your departure city, arrival city, route, date and time, available seats (1-6), and fare, then tap 'Offer Ride'. The departure and arrival cities must be different, and the date must be in the future."
    },
    {
      id: 4,
      question: "Can I post multiple ride offers at once?",
      answer: "Yes — you can have up to 5 upcoming rides at a time, so commuters can post their whole week. The only limit: two of your rides can't depart within 2 hours of each other (that's treated as a duplicate — edit the existing one instead)."
    },
    {
      id: 5,
      question: "How do I request a ride?",
      answer: "If you can't find a ride that suits you, go to Publish, switch to 'Request', fill in your route, date and time, and the fare YOU are willing to pay, then tap 'Request Ride'. You can have up to 3 open requests at a time."
    },
    {
      id: 6,
      question: "What happens after I post a request?",
      answer: "Your request appears in the feed with your offered fare. Any driver can tap 'I can take you' — that instantly creates their ride for your trip at your fare, books you onto it, and notifies you. From there it works like any booking: chat with the driver, track the ride, and review them afterwards."
    },
    {
      id: 7,
      question: "How do I book a seat on someone's ride?",
      answer: "Find a ride on the feed, tap it to view details, choose how many seats you need, and hit 'Book'. The ride must have enough free seats and must not have started yet. You can't book your own ride, and women-only rides can only be booked by women."
    },
    {
      id: 8,
      question: "Can I cancel my booking?",
      answer: "Yes, anytime before the ride starts — and it's completely free if you cancel more than 24 hours before departure. Cancelling within the last 24 hours leaves the driver a seat they can't refill, so it's counted as a 'late unbook' on your profile. Once the ride is in transit, bookings are locked."
    },
    {
      id: 9,
      question: "What happens when a ride is started?",
      answer: "The ride goes 'In Transit': no new bookings, no unbooking, no editing. The driver collects each passenger's boarding code at pickup — the Complete button only unlocks once every passenger is verified or marked a no-show. If the driver started the ride by mistake, they have 10 minutes to undo it."
    },
    {
      id: 10,
      question: "What is the boarding code?",
      answer: "The moment you book a seat you get a personal code (like SR-ABC123), shown on the ride page and re-sent when the ride starts. Tell it to your driver when you get in the car — they type it in, and that one step proves the ride for both of you: your attendance is verified immediately, and your arrival is confirmed automatically 24 hours after the ride completes (unless you dispute). The driver cannot even complete the ride without collecting everyone's code, so this is never optional for them. Never share it with anyone but your driver."
    },
    {
      id: 11,
      question: "Do I need to do anything after the ride?",
      answer: "No — if your boarding code was entered at pickup, you're done. Your arrival confirms on its own within 24 hours, or you can tap 'Confirm arrival' to make it immediate. If something went wrong, dispute the ride within that window — disputing needs no code and alerts our admins."
    },
    {
      id: 12,
      question: "What if the driver forgets to start or complete the ride?",
      answer: "No one gets punished for forgetting a button. A ride that's never started quietly expires 6 hours after its departure time — no penalty, bookings released, and the driver can post again immediately. A ride still 'in transit' 24 hours after departure gets a reminder and is then completed automatically. If the driver never collected boarding codes, those passengers simply close out as neutral — nobody is marked a no-show, and neither side gets to review the other."
    },
    {
      id: 13,
      question: "Can I delete my ride?",
      answer: "Yes, any time before it starts. Deleting is free if the ride is more than 24 hours away or has no bookings. Deleting a ride with booked passengers inside the final 24 hours counts as a cancellation on your profile. In-transit and completed rides can't be deleted."
    },
    {
      id: 14,
      question: "What happens to passengers if I delete my ride?",
      answer: "All booked passengers are notified immediately and their bookings are released, so the ride disappears from their lists too. They can book another ride right away."
    },
    {
      id: 15,
      question: "How do reviews work?",
      answer: "After a ride is completed, the driver and passengers can rate each other (1-5 stars, one review per person per ride). To keep reviews honest, both directions unlock from the same proof: the boarding code entered at pickup. The driver can only review verified passengers, and a passenger can only review the driver once their own boarding was verified."
    },
    {
      id: 16,
      question: "What do the numbers on my profile mean?",
      answer: "Your profile shows completed rides, rides taken, and your reliability: cancellations (rides you cancelled late with passengers), late unbooks (bookings you cancelled in the last 24 hours), no-shows (times a driver marked you absent), and disputes (times a passenger reported your ride didn't go as claimed). Ride carefully and they stay at zero."
    },
    {
      id: 17,
      question: "How can I reset my password?",
      answer: "Tap 'Forgot Password?' on the login page to receive a reset link via email. The link is valid for 15 minutes. Click it, enter your new password, and you're good to go."
    },
    {
      id: 18,
      question: "Why am I unable to post rides or book seats?",
      answer: "This can happen if your account is temporarily banned, your email isn't verified, you've hit the posting limit (5 upcoming rides / 3 open requests), or you already have a ride departing within 2 hours of the new one. The error message links to the ride that's blocking you."
    },
    {
      id: 19,
      question: "Is my personal information secure?",
      answer: "Yes. Your password is hashed and never stored in plain text. Sessions are encrypted, your phone number is only shown to people on your ride, and live location is shared only with your ride's members while in transit."
    },
  ];

  const handleFaqClick = (id) => {
    setSelectedFaq(selectedFaq === id ? null : id);
  };

  return (
    <div className="sr-faqs">
      <Helmet>
        <title>FAQs - SastiRide Ride Sharing</title>
        <meta name="description" content="Common questions about booking rides, sharing travel costs, safety, and payments on SastiRide - Pakistan's carpooling platform." />
        <link rel="canonical" href="https://sastiride.com/faqs" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>
      <div className="sr-faqs__top">
        <h1 className="sr-faqs__title">FAQs</h1>
        <p className="sr-faqs__subtitle">Frequently asked questions</p>
      </div>

      <div className="sr-faqs__body">
        <div className="sr-faqs__container">
          {faqs.map((faq) => (
            <div key={faq.id} className="sr-faqs__item" onClick={() => handleFaqClick(faq.id)}>
              <h2>{faq.question}</h2>
              {selectedFaq === faq.id && <p>{faq.answer}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Faqs;
